import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Plus, X } from "lucide-react";
import { api } from "../lib/api";
import { BusinessCard } from "../components/BusinessCard";
import { SubmitBusinessModal } from "../components/SubmitBusinessModal";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);

  const search = params.get("search") || "";
  const category = params.get("category") || "";
  const city = params.get("city") || "";

  const [q, setQ] = useState(search);

  useEffect(() => {
    api.categories().then(setCats).catch(() => {});
    api.markets().then(setMarkets).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = {};
    if (search) p.search = search;
    if (category) p.category = category;
    if (city) p.city = city;
    api.businesses(p).then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  }, [search, category, city]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQ(search); }, [search]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const submitSearch = (e) => { e.preventDefault(); setParam("search", q); };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Sticky search */}
      <div className="sticky top-16 z-30 -mx-4 bg-background/95 px-4 py-3 backdrop-blur-md sm:mx-0 sm:rounded-2xl sm:px-0">
        <form onSubmit={submitSearch} className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="explore-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar negocio, categoría o ciudad..."
            className="h-12 w-full rounded-full border border-input bg-white pl-12 pr-4 text-base outline-none focus:ring-2 focus:ring-brand/40"
          />
        </form>

        {/* Category chips */}
        <div className="ml-scroll mt-3 flex gap-2 overflow-x-auto pb-1">
          <Chip active={!category} onClick={() => setParam("category", "")}>Todas</Chip>
          {cats.map((c) => (
            <Chip key={c.slug} active={category === c.slug} testid={`chip-${c.slug}`} onClick={() => setParam("category", category === c.slug ? "" : c.slug)}>
              {c.name}
            </Chip>
          ))}
        </div>
        {/* City filter */}
        <div className="ml-scroll mt-2 flex items-center gap-2 overflow-x-auto pb-1">
          <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Chip active={!city} onClick={() => setParam("city", "")}>Todo EE.UU.</Chip>
          {markets.map((m) => (
            <Chip key={m.slug} active={city === m.city} testid={`city-${m.slug}`} onClick={() => setParam("city", city === m.city ? "" : m.city)}>
              {m.label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="results-count">
          {loading ? "Buscando..." : `${items.length} resultado${items.length === 1 ? "" : "s"}`}
        </p>
        <button
          data-testid="open-submit-business"
          onClick={() => setSubmitOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-white"
        >
          <Plus className="h-4 w-4" /> Agregar negocio
        </button>
      </div>

      {!loading && items.length === 0 && (
        <div className="mt-16 flex flex-col items-center text-center">
          <X className="h-10 w-10 text-muted-foreground" />
          <p className="mt-3 font-semibold">No encontramos negocios</p>
          <p className="text-sm text-muted-foreground">Prueba con otra categoría o ciudad.</p>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((b) => (
          <BusinessCard key={b.id} b={b} onContact={(id, type) => api.track({ business_id: id, type })} />
        ))}
      </div>

      <SubmitBusinessModal open={submitOpen} onClose={() => setSubmitOpen(false)} cats={cats} markets={markets} />
    </div>
  );
}

const Chip = ({ children, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
      active ? "border-brand bg-brand text-white" : "border-border bg-white text-brand-ink hover:border-brand/40"
    }`}
  >
    {children}
  </button>
);
